import { useState, useEffect } from 'react';
import { Target, Circle, CheckCircle2, ChevronRight, Trophy, Sparkles } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import { useNavigate } from 'react-router-dom';

interface ActionPlan {
  id: string;
  goal: string;
  progress_percentage: number;
  action_items: Array<{ id: string; text: string; status: 'pending' | 'completed' }>;
}

export function ActivePlansWidget() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<ActionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    loadPlan();
    setMounted(true);
  }, []);

  async function loadPlan() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_action_plans')
        .select('id, goal, progress_percentage, action_items')
        .eq('user_id', user.id)
        .in('status', ['in_progress', 'not_started'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setPlan(data);
    } catch (error) {
      logger.error('Error loading plan:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCompleteAction(actionId: string) {
    if (!plan || completing) return;

    setCompleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updatedItems = plan.action_items.map(item =>
        item.id === actionId ? { ...item, status: 'completed' as const } : item
      );

      const completedCount = updatedItems.filter(i => i.status === 'completed').length;
      const newProgress = Math.round((completedCount / updatedItems.length) * 100);

      const { error } = await supabase
        .from('user_action_plans')
        .update({
          action_items: updatedItems,
          progress_percentage: newProgress,
          status: newProgress === 100 ? 'completed' : 'in_progress'
        })
        .eq('id', plan.id);

      if (error) throw error;

      const today = new Date().toISOString().split('T')[0];
      await supabase.rpc('update_daily_activity', {
        p_user_id: user.id,
        p_activity_date: today,
        p_action_taken: true
      });

      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 1500);

      setPlan({
        ...plan,
        action_items: updatedItems,
        progress_percentage: newProgress
      });
    } catch (error) {
      logger.error('Error completing action:', error);
    } finally {
      setCompleting(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-6">
        <div className="flex justify-center py-8">
          <div className="relative">
            <div className="w-10 h-10 rounded-full border-2 border-gray-100" />
            <div className="absolute inset-0 w-10 h-10 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return null;
  }

  const nextAction = plan.action_items.find(item => item.status === 'pending');
  const completedCount = plan.action_items.filter(i => i.status === 'completed').length;
  const totalCount = plan.action_items.length;
  const remainingCount = totalCount - completedCount;
  const isComplete = completedCount === totalCount;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 transition-all duration-700 ease-out"
            style={{ width: `${plan.progress_percentage}%` }}
          />
        </div>

        <div className="p-5 pt-6">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                  isComplete
                    ? 'bg-gradient-to-br from-green-400 to-emerald-500'
                    : 'bg-gradient-to-br from-amber-100 to-orange-100'
                }`}>
                  {isComplete ? (
                    <Trophy className="h-6 w-6 text-white" />
                  ) : (
                    <Target className="h-6 w-6 text-orange-600" />
                  )}
                </div>
                {justCompleted && (
                  <div className="absolute -top-1 -right-1">
                    <Sparkles className="h-5 w-5 text-amber-500 animate-pulse" />
                  </div>
                )}
              </div>
              <div className="min-w-0 pt-0.5">
                <h3 className="font-semibold text-gray-900 leading-tight mb-1">{plan.goal}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{completedCount} of {totalCount}</span>
                  {!isComplete && remainingCount <= 2 && (
                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      {remainingCount} to go!
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/plans')}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors p-2 -m-2 rounded-lg hover:bg-gray-50"
            >
              <span>All</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-1.5 mb-5">
            {plan.action_items.map((item, index) => (
              <div
                key={item.id}
                className={`flex-1 h-2 rounded-full transition-all duration-500 ${
                  item.status === 'completed'
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                    : 'bg-gray-100'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              />
            ))}
          </div>

          {nextAction ? (
            <button
              onClick={() => handleCompleteAction(nextAction.id)}
              disabled={completing}
              className="w-full group"
            >
              <div className={`relative flex items-center gap-4 p-4 rounded-xl border-2 border-dashed transition-all duration-300 ${
                completing
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-gray-200 hover:border-amber-400 hover:bg-amber-50/50'
              }`}>
                <div className="relative flex-shrink-0">
                  {completing ? (
                    <div className="w-6 h-6 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
                  ) : (
                    <>
                      <Circle className="h-6 w-6 text-gray-300 group-hover:text-amber-400 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <CheckCircle2 className="h-6 w-6 text-amber-500" />
                      </div>
                    </>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-gray-700 group-hover:text-gray-900 transition-colors leading-snug">
                    {nextAction.text}
                  </p>
                </div>
                <span className="text-xs font-medium text-gray-400 group-hover:text-amber-600 transition-colors whitespace-nowrap">
                  {completing ? 'Saving...' : 'Tap to complete'}
                </span>
              </div>
            </button>
          ) : (
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 p-4">
              <div className="absolute top-0 right-0 w-24 h-24 bg-green-200/30 rounded-full -mr-12 -mt-12 blur-2xl" />
              <div className="relative flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Goal complete!</p>
                  <p className="text-sm text-green-600">Amazing work on this one</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
